/**
 * Shared helpers for Application Discovery UI (coverage status, filters, counts).
 */

export const COVERAGE_FULL = "full";
export const COVERAGE_PARTIAL = "partial";
export const COVERAGE_NONE = "none";

export const TAB_KEY_ONBOARDED = "onboarded";
export const TAB_KEY_DISCOVERY = "discovery";
export const TAB_KEY_ALL = "all";
export const TAB_KEY_UNTAGGED = "untagged";

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function normalizeCoverageStatus(value) {
  const normalized = normalizeText(value);
  if (normalized === COVERAGE_FULL || normalized === "complete" || normalized === "onboarded") {
    return COVERAGE_FULL;
  }
  if (normalized === COVERAGE_PARTIAL) {
    return COVERAGE_PARTIAL;
  }
  return COVERAGE_NONE;
}

/**
 * Derive coverage from counters (same rule as backend derive_coverage_status).
 */
export function deriveCoverageStatus({
  logsOnboarded = 0,
  logsTotal = 0,
  metricsOnboarded = 0,
  metricsTotal = 0,
} = {}) {
  const total = Number(logsTotal || 0) + Number(metricsTotal || 0);
  const onboarded = Number(logsOnboarded || 0) + Number(metricsOnboarded || 0);
  if (total <= 0 || onboarded <= 0) return COVERAGE_NONE;
  if (onboarded >= total) return COVERAGE_FULL;
  return COVERAGE_PARTIAL;
}

export function getApplicationCounts(app = {}) {
  return {
    logsOnboarded: Number(app.logs_onboarded || 0),
    logsTotal: Number(app.logs_total || 0),
    metricsOnboarded: Number(app.metrics_onboarded || 0),
    metricsTotal: Number(app.metrics_total || 0),
    resourceCount: Number(app.resource_count || (app.resources || []).length || 0),
  };
}

export function getApplicationOnboardingState(app = {}) {
  const counts = getApplicationCounts(app);
  const explicit = normalizeCoverageStatus(app.status);
  const derived = deriveCoverageStatus(counts);
  // Prefer explicit status when present and valid; otherwise derive.
  const status =
    app.status != null && String(app.status).trim() !== "" ? explicit : derived;
  return { ...counts, status };
}

export function getApplicationOnboardingBreakdown(applications = []) {
  const breakdown = { total: 0, full: 0, partial: 0, none: 0 };
  for (const app of applications || []) {
    const { status } = getApplicationOnboardingState(app);
    breakdown.total += 1;
    if (status === COVERAGE_FULL) breakdown.full += 1;
    else if (status === COVERAGE_PARTIAL) breakdown.partial += 1;
    else breakdown.none += 1;
  }
  return breakdown;
}

export function filterApplicationsByTab(applications = [], tab = TAB_KEY_ALL) {
  if (tab === TAB_KEY_ALL) return applications;
  if (tab === TAB_KEY_ONBOARDED) {
    return applications.filter((app) => {
      const { status } = getApplicationOnboardingState(app);
      return status === COVERAGE_FULL || status === COVERAGE_PARTIAL;
    });
  }
  if (tab === TAB_KEY_DISCOVERY) {
    return applications.filter((app) => {
      const { status } = getApplicationOnboardingState(app);
      return status === COVERAGE_NONE;
    });
  }
  return applications;
}

export function filterApplicationsByCoverage(applications = [], coverageFilter = "all") {
  if (!coverageFilter || coverageFilter === "all") return applications;
  const wanted = normalizeCoverageStatus(coverageFilter);
  return applications.filter((app) => getApplicationOnboardingState(app).status === wanted);
}

export function filterApplicationsBySearch(applications = [], search = "") {
  const q = normalizeText(search);
  if (!q) return applications;
  return applications.filter((app) => {
    const name = normalizeText(app.name || app.application_name);
    const slug = normalizeText(app.app_slug);
    const regions = (app.regions || []).join(" ").toLowerCase();
    return name.includes(q) || slug.includes(q) || regions.includes(q);
  });
}

export function mergeStatsWithBreakdown(stats, applications = []) {
  const fromApps = getApplicationOnboardingBreakdown(applications);
  const byStatus = stats?.by_coverage_status || {};
  return {
    total: Number(stats?.applications_total ?? fromApps.total),
    full: Number(byStatus.full ?? fromApps.full),
    partial: Number(byStatus.partial ?? fromApps.partial),
    none: Number(byStatus.none ?? fromApps.none),
    unassignedCount: Number(stats?.unassigned_count || 0),
    conflictsCount: Number(stats?.conflicts_count || 0),
    resourcesTotal: Number(stats?.resources_total || 0),
    lastDiscoveredAt: stats?.last_discovered_at || null,
  };
}

export function parseTagsUnreadableError(error) {
  const status = error?.status;
  const body = error?.responseBody;
  if (status !== 403) return null;
  let parsed = null;
  try {
    parsed = typeof body === "string" ? JSON.parse(body) : body;
  } catch {
    parsed = null;
  }
  const detail = parsed?.detail || parsed;
  const errorCode = detail?.error || parsed?.error;
  if (errorCode !== "tags_unreadable" && !String(body || "").includes("tags_unreadable")) {
    if (!String(body || error?.message || "").includes("tag:GetResources")) {
      return null;
    }
  }
  const action = detail?.action || "tag:GetResources";
  const region = detail?.region || "";
  const message =
    detail?.message ||
    error?.message ||
    `Cannot read resource tags (${action}). Update the observability IAM role for this account.`;
  return { action, region, message };
}

export function appDisplayName(app = {}) {
  return app.name || app.application_name || app.app_slug || "Unknown";
}

const SERVICE_LABELS = {
  lambda: "Lambda",
  ecs: "ECS",
  eks: "EKS",
  ec2: "EC2",
  logs: "CloudWatch Logs",
  s3: "S3",
  sqs: "SQS",
  sns: "SNS",
  dynamodb: "DynamoDB",
  rds: "RDS",
  "cognito-idp": "Cognito",
  cognito: "Cognito",
  aoss: "OpenSearch Serverless",
  es: "OpenSearch",
  opensearch: "OpenSearch",
  ses: "SES",
  scheduler: "EventBridge Scheduler",
  events: "EventBridge",
  apigateway: "API Gateway",
  elasticloadbalancing: "ELB",
  states: "Step Functions",
  kinesis: "Kinesis",
  firehose: "Firehose",
  elasticache: "ElastiCache",
  generic: "Other",
};

export function serviceKeyFromArn(arn) {
  const parts = String(arn || "").split(":");
  return parts.length >= 3 ? normalizeText(parts[2]) : "";
}

/**
 * Prefer stored service_key; if collapsed to generic, recover from ARN.
 */
export function resolveResourceServiceKey(resource = {}) {
  const key = normalizeText(resource.service_key);
  if (key && key !== "generic" && key !== "other") {
    return key;
  }
  return serviceKeyFromArn(resource.arn) || "generic";
}

export function formatServiceKey(serviceKey) {
  const key = normalizeText(serviceKey);
  if (!key) return "Unknown";
  if (SERVICE_LABELS[key]) return SERVICE_LABELS[key];
  // cognito-idp → Cognito-Idp → nicer title case of raw prefix
  return String(serviceKey)
    .split("-")
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(" ");
}

/**
 * Prefer Name tag, then resource.name, then ARN trailing segment.
 */
export function resourceDisplayName(resource = {}) {
  const tags = resource.tags || {};
  const tagged =
    (tags.Name && String(tags.Name).trim()) ||
    (tags.name && String(tags.name).trim());
  if (tagged) return tagged;
  if (resource.name && String(resource.name).trim()) {
    return String(resource.name).trim();
  }
  const arn = String(resource.arn || "");
  if (arn.includes("/")) return arn.split("/").pop();
  if (arn.includes(":")) return arn.split(":").pop();
  return "—";
}

/**
 * Count resources by service_key → "3 Lambda, 1 ECS".
 */
export function getResourceSummary(app = {}) {
  const resources = app.resources || [];
  if (!resources.length) {
    const count = Number(app.resource_count || 0);
    return count > 0 ? `${count} resource${count === 1 ? "" : "s"}` : "";
  }
  const counts = {};
  for (const r of resources) {
    const key = resolveResourceServiceKey(r);
    counts[key] = (counts[key] || 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([key, n]) => `${n} ${formatServiceKey(key)}`)
    .join(", ");
}

/**
 * Dominant service type for a Type badge.
 */
export function getDominantServiceType(app = {}) {
  const resources = app.resources || [];
  if (!resources.length) return null;
  const counts = {};
  for (const r of resources) {
    const key = resolveResourceServiceKey(r);
    counts[key] = (counts[key] || 0) + 1;
  }
  let best = null;
  let bestN = 0;
  for (const [key, n] of Object.entries(counts)) {
    if (n > bestN) {
      best = key;
      bestN = n;
    }
  }
  return best;
}

/** Escape a value for use inside a PromQL double-quoted label matcher. */
export function escapePromLabelValue(value = "") {
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Build a Grafana Explore PromQL selector for an application metric target.
 * YACE exports AWS tags as tag_<Key> and dimensions as dimension_<Name>.
 */
export function buildApplicationMetricExploreExpr(app = {}, metricTarget = {}) {
  const appName = String(
    app?.name || app?.application_name || app?.app_slug || ""
  ).trim();
  const tagKey = String(
    app?.application_tag_key || "ApplicationName"
  ).trim() || "ApplicationName";
  const parts = [];
  if (appName) {
    parts.push(`tag_${tagKey}="${escapePromLabelValue(appName)}"`);
  }
  const dims =
    metricTarget?.dimensions && typeof metricTarget.dimensions === "object"
      ? metricTarget.dimensions
      : {};
  Object.entries(dims).forEach(([key, value]) => {
    if (value == null || value === "") return;
    parts.push(
      `dimension_${key}="${escapePromLabelValue(String(value))}"`
    );
  });
  const region = String(metricTarget?.region || "").trim();
  if (region) {
    parts.push(`region="${escapePromLabelValue(region)}"`);
  }
  return parts.length ? `{${parts.join(",")}}` : "";
}

/**
 * Short display name for a log target selector (ARN, log group path, or s3 URI).
 */
export function logTargetDisplayName(selector = "") {
  const raw = String(selector || "").trim();
  if (!raw) return "—";
  if (raw.startsWith("s3://")) {
    const without = raw.slice(5);
    const slash = without.indexOf("/");
    return slash >= 0 ? without.slice(0, slash) || without : without;
  }
  if (raw.includes(":log-group:")) {
    const after = raw.split(":log-group:")[1] || "";
    const name = after.split(":")[0] || after;
    const trimmed = name.replace(/^\//, "");
    const parts = trimmed.split("/");
    return parts[parts.length - 1] || trimmed || name;
  }
  if (raw.startsWith("/")) {
    const parts = raw.replace(/^\//, "").split("/");
    return parts[parts.length - 1] || raw;
  }
  if (raw.includes("/")) return raw.split("/").pop();
  return raw;
}

/**
 * UX Source label for a log target kind.
 */
export function logTargetSourceLabel(kind = "cloudwatch") {
  const k = normalizeText(kind);
  if (k === "s3") return "S3";
  if (k === "loki") return "Loki";
  return "CloudWatch";
}

/**
 * Friendly metric row title: namespace + primary dimension value when present.
 */
export function metricTargetDisplayName(mt = {}) {
  const ns = String(mt.namespace || "").trim() || "Metric";
  const dims = mt.dimensions || {};
  const preferredKeys = [
    "FunctionName",
    "UserPool",
    "DomainName",
    "CollectionId",
    "TableName",
    "QueueName",
    "TopicName",
    "ClusterName",
    "ServiceName",
    "AutoScalingGroupName",
    "DBInstanceIdentifier",
    "StateMachineArn",
    "ScheduleGroup",
    "Name",
  ];
  for (const key of preferredKeys) {
    const val = dims[key];
    if (val != null && String(val).trim()) {
      const short =
        key === "StateMachineArn"
          ? String(val).split(":").pop()
          : String(val).trim();
      return `${ns} · ${short}`;
    }
  }
  const first = Object.values(dims).find((v) => v != null && String(v).trim());
  if (first) return `${ns} · ${String(first).trim()}`;
  return ns;
}

export function coverageDisplayLabel(status) {
  const s = normalizeCoverageStatus(status);
  if (s === COVERAGE_FULL) return "Full";
  if (s === COVERAGE_PARTIAL) return "Partial";
  return "Not onboarded";
}

export function coverageTagColor(status) {
  const s = normalizeCoverageStatus(status);
  if (s === COVERAGE_FULL) return "success";
  if (s === COVERAGE_PARTIAL) return "warning";
  return "default";
}

export function serviceTypeTagColor(serviceKey) {
  const key = normalizeText(serviceKey);
  if (key === "lambda") return "purple";
  if (key === "ecs") return "blue";
  if (key === "eks") return "cyan";
  if (key === "ec2") return "orange";
  if (key === "cognito-idp" || key === "cognito") return "magenta";
  if (key === "aoss" || key === "es" || key === "opensearch") return "geekblue";
  if (key === "ses") return "gold";
  if (key === "scheduler" || key === "events") return "lime";
  if (key === "s3") return "green";
  return "default";
}

/** Match firing/configured alerts to an application via common label keys. */
export function alertMatchesApplication(alert, appSlug, appName) {
  const labels =
    alert?.labels ||
    alert?.alert?.labels ||
    alert?.annotations ||
    {};
  const values = [
    labels.application,
    labels.app,
    labels.app_slug,
    labels.ApplicationName,
    labels.tag_ApplicationName,
    labels.application_name,
  ]
    .map((v) => normalizeText(v))
    .filter(Boolean);
  const targets = [appSlug, appName].map((v) => normalizeText(v)).filter(Boolean);
  if (!targets.length || !values.length) return false;
  return values.some((v) => targets.some((t) => v === t || v.includes(t) || t.includes(v)));
}
